<template>
  <div class=" min-h-screen bg-white">
    <!-- Header -->
    <div class="flex items-center px-4 md:px-8 lg:px-24 py-6 border-bottom">
      <!-- Replace with your logo images -->
      <img
        class="w-10 mr-4 fill-current text-primary"
        :src="require('../../../../public/Group-543.svg')"
      />
      <img
        :src="require('../../../../public/Group-544.svg')"
        width="125px"
      />
    </div>

    <!-- Main Section Below Header -->
    <div class=" main-container flex flex-col w-full mt-8 mb-4">
      <div class=" pb-8 border-bottom">

        <!-- Status Badge -->
        <div class="mb-4 text-center text-base md:text-left md:text-lg lg:text-xl ">
          <span class="status-text" v-if="status === 'approved'">
            Order Successful
          </span>
          <span class="status-text" v-else-if="status === 'rejected'">
            No Order Placed
          </span>
          <span class="status-text" v-else-if="status === 'pending'">
            {{ pendingHeading }}
          </span>
          <span class="status-text" v-else-if="loadError">
            Couldn't Load Request
          </span>
          <span class="status-text" v-else-if="loaded">
            Request No Longer Available
          </span>
          <span class="status-text" v-else>
            Loading Request...
          </span>
        </div>

        <!-- Info - Dynamic -->
        <div class="text-gray-800 text-base text-center md:text-left">
          <div v-if="status === 'approved'">
            Your order has been successfully placed: <b>{{ requestNumber }}</b>
          </div>
            <div v-else-if="status === 'rejected'">
            <div>
              You have determined not to place an order based on the submitted Inventory Forecast.
            </div>
            <div class="mt-4">
              <b>{{ clinicName }}</b> has been notified of your decision. Please reach out to the clinic to discuss appropriate next steps.
            </div>
            </div>
          <div v-else-if="status === 'pending'">
            <div v-if="isApproveLink">
              Review the order requested by <b>{{ clinicName }}</b> below. The order is placed only when you confirm.
            </div>
            <div v-else>
              Review the Inventory Forecast from <b>{{ clinicName }}</b> below. Confirm to reject it: no order will be placed and the clinic will be notified.
            </div>
          </div>
          <div v-else-if="loadError">
            <div>{{ loadError }}</div>
            <div class="mt-4">
              Please try the link again shortly. If it keeps failing, use the link in the most recent email about this request.
            </div>
          </div>
          <div v-else-if="loaded">
            This request has already been actioned or has expired, so it can no longer be changed.
          </div>
          <div v-else>
            We’re loading your request. This will only take a short while.
          </div>
        </div>
      </div>

      <!-- Order Card Section -->
      <OrderCard
        :order-rows="orderRows"
        :total-amount="totalAmount"
        :status="status"
        :decision="decision"
      />

      <!-- Explicit confirmation: the decision is sent only on this click,
           never on page load, so a mail scanner that opens the link can't action it.
           The link is signed for one decision, so only that decision is offered. -->
      <div v-if="status === 'pending'" class="flex flex-col items-center md:items-start mb-8">
        <vs-button
          :color="isApproveLink ? 'primary' : 'danger'"
          :disabled="submitting"
          @click="confirmDecision"
        >
          {{ actionLabel }}
        </vs-button>
        <div class="text-gray-500 text-sm mt-3">
          Meant to {{ otherLinkLabel.toLowerCase() }} instead? Use the
          {{ otherLinkLabel }} link in the email.
        </div>
      </div>

        <!-- Info - Dynamic -->
      <div v-if="status === 'approved'" class="text-gray-800 text-base text-center md:text-left">
        A copy of your invoice and tracking details will be sent to you and your clinic by emails once available.
      </div>
    </div>
    <!-- Footer -->
    <footer class="w-full flex flex-col items-center justify-center py-8">
      <div class="text-gray-500 text-base">Powered by</div>
      <div class="flex items-center mt-4">
        <!-- Logo Placeholder -->
        <div class=" flex items-center">
          <img
            class="w-10 mr-4 fill-current text-primary"
            :src="require('../../../../public/Group-543.svg')"
          />
          <img
            :src="require('../../../../public/Group-544.svg')"
            width="125px"
          />
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import OrderCard from './OrderCard.vue';
import { mapActions } from 'vuex';

export default {
  name: 'ResponseStatusPage',
  components: {
    OrderCard,
  },
  data() {
    return {
      nurseName: '',
      nurseEmail: '',
      notes: '',
      status: '',
      orderRows: [],
      totalAmount: 0,
      requestNumber: null,
      clinicName: null,
      loaded: false,
      loadError: null,
      submitting: false,
    };
  },
  props: {
    decision: { // 'approved' or 'rejected'
      type: String,
      required: true,
      validator: value => ['approved', 'rejected'].includes(value),
    },
    requestId: { 
      type: [String, Number],
      required: true,
    },
    approverId: {
      type: [String, Number],
      required: true,
    },
    token: {
      type: String,
      default: null,
    },

  },
  computed: {
    isApproveLink() {
      return this.decision === 'approved';
    },
    pendingHeading() {
      return `${this.isApproveLink ? 'Approve' : 'Reject'} S4 Order Request`;
    },
    actionLabel() {
      return this.isApproveLink ? 'Approve and place order' : 'Reject request';
    },
    otherLinkLabel() {
      return this.isApproveLink ? 'Reject' : 'Approve';
    },
  },
  methods: {
    ...mapActions('orderRequestApproval', ['getOrderRequestApproval', 'updateOrderRequestApproval']),
    async fetchOrderRequestApproval(requestId) {
      
      const { data } = await this.getOrderRequestApproval({ requestId, token: this.token });
      if (data.data.length > 0) {
        const request = data.data[0];
        this.nurseName = request.requester.firstName + ' ' + request.requester.lastName;
        this.nurseEmail = request.requester.email;
        this.status = request.status.toLowerCase();
        this.orderRows = request.checkoutMetadata.cartProductStore;
        this.totalAmount = request.checkoutMetadata.checkoutPaymentPayload.charge.amount
        ;
        this.requestNumber = request.requestNumber;
        this.clinicName = request.clinic.clinicName;
      }
    },
    // The global axios interceptor rejects with the response itself ({ status, data }).
    apiMessage(error) {
      const data = (error && (error.data || (error.response && error.response.data))) || {};
      return data.message;
    },
    // Load-only: never decides. See confirmDecision.
    async initialize() {
      try {
        this.$vs.loading();
        await this.fetchOrderRequestApproval(this.requestId);
      } catch (error) {
        // Shown in the page instead of "no longer available": the request may
        // still be pending (network error, 5xx) or the link may be bad (403).
        this.loadError = this.apiMessage(error) || 'Failed to load the order approval request.';
        console.error((error && error.data) || error);
      } finally {
        this.loaded = true;
        this.$vs.loading.close();
      }
    },
    async confirmDecision() {
      if (this.submitting || this.status !== 'pending') return;
      this.submitting = true;
      this.$vs.loading();
      try {
        await this.updateOrderRequestApproval(
          { requestId: this.requestId,
            status: this.decision,
            approverId: this.approverId,
            token: this.token,
          });
        this.status = this.decision;
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: this.apiMessage(error) || `Failed to ${this.isApproveLink ? 'approve' : 'reject'} order approval request.`,
          color: 'danger',
        });
        console.error((error && error.data) || error);
        // The request may have been decided elsewhere or expired since it loaded;
        // re-read it so the page shows the current state instead of a dead button.
        await this.fetchOrderRequestApproval(this.requestId).catch(() => {});
      } finally {
        this.submitting = false;
        this.$vs.loading.close();
      }
    },
  },
  created() {
    this.initialize();
  },
  
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #ECE9E6;
}

.main-container {
  padding: 0 20%;

}

.status-text {
  color: #256A54;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: 0.28px;
}
</style> 
